import { useEffect, useState } from "react";
import TextInput from "../components/basicComponents/text-input";
import ImageUpload from "../components/basicComponents/image-upload";
import Container from "../components/basicComponents/container";
import TextEditor from "../components/basicComponents/text-editor";
import Select from "../components/basicComponents/select";
import * as Icons from "../assets/SVGs/index";
import Button from "../components/basicComponents/button";
import { useSelector } from "react-redux";
import { selectGlobalState } from "../state-management/selectors";
import { ProjectDataProps } from "../types/public";
import ToggleSwitch from "./basicComponents/switch-box";
import VideoUpload from "../components/basicComponents/video-upload";
import PersianDatePicker from "../components/basicComponents/persian-datepicker";
import PdfUpload from "../components/basicComponents/Pdf-upload"; // Import the PdfUpload component
import { Moment } from "moment-jalaali";
import TextArea from "../components/basicComponents/text-area"; // Import TextArea component

type SubmitHandler = (event: React.FormEvent<HTMLFormElement>) => void;

interface ProjectComponentProps {
  handleSubmit: SubmitHandler;
  formData: ProjectDataProps;
  setFormData: React.Dispatch<React.SetStateAction<ProjectDataProps>>;
  currentUserId?: string;
  loading: boolean;
}

export default function ProjectComponent({
  handleSubmit,
  formData,
  setFormData,
  currentUserId,
  loading,
}: ProjectComponentProps) {
  const { departmentsData, groupingData } = useSelector(selectGlobalState);
  const [departmentOptions, setDepartmetOptions] = useState([
    { label: "", value: "" },
  ]);

  const permissionDepartment = groupingData.find(
    (item: any) => item.user_id === currentUserId
  )?.department_id || ["none"];

  useEffect(() => {
    setDepartmetOptions(
      departmentsData
        .filter((item: any) => permissionDepartment.includes(item._id))
        .map((art: any) => ({
          label: art.name,
          value: art._id,
        }))
    );
  }, [departmentsData]);

  const handlePdfFilesSelect = (files: any) => {
    setFormData((prev) => ({
      ...prev,
      pdf: { ...files },
    }));
  };

  return (
    <div className="flex flex-col items-center w-full">
      <Container header="ثبت پروژه ">
        <form className="w-full" onSubmit={handleSubmit}>
          <div className="flex flex-col md:flex-row gap-2">
            <div className="mb-5 mt-5 md:w-[33.3%] w-full">
              <TextInput
                name="title"
                value={formData.title}
                placeholder="عنوان پروژه"
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }));
                }}
                label="عنوان پروژه"
                RightIcon={<Icons.TitleIcon className="w-5 h-5" />}
              />
            </div>
            <div className="mb-5 mt-5 md:w-[33.3%] w-full">
              <PersianDatePicker
                selectedDate={formData.start_date}
                onDateChange={(date: Moment | undefined) =>
                  setFormData((prev) => ({
                    ...prev,
                    start_date: date,
                  }))
                }
              />
            </div>
            <div className="mb-5 mt-5 md:w-[33.3%] w-full">
              <Select
                name="departments"
                options={departmentOptions}
                value={formData.department_id}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    department_id: e.target.value,
                  }));
                }}
                placeholder="گروه پروژه  "
                className="border"
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-start gap-2">
            <div className="md:w-[33.3%] w-full">
              {/* Add TextArea component */}
              <TextArea
                className=""
                name="tags"
                value={formData.tags}
                placeholder="تگ ها "
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    tags: e.target.value,
                  }));
                }}
                rows={4}
              />
            </div>
            <div className="md:w-[33.3%] w-full">
              <ImageUpload
                onImageChange={(file) => {
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      const base64String = reader.result as string;
                      setFormData((prev: any) => ({
                        ...prev,
                        index_picture: base64String,
                      }));
                      localStorage.setItem("ProjectImageBase64", base64String);
                    };
                    reader.readAsDataURL(file);
                  } else {
                    setFormData((prev: any) => ({
                      ...prev,
                      index_picture: "",
                    }));
                    localStorage.removeItem("ProjectImageBase64");
                  }
                }}
                storageKey="ProjectImageBase64"
                defaultImage={formData.index_picture}
              />
            </div>

            <div className="md:w-[33.3%] w-full">
              <VideoUpload
                onVideoChange={(file) => {
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      const base64String = reader.result as string;
                      setFormData((prev: any) => ({
                        ...prev,
                        index_video: base64String,
                      }));
                      localStorage.setItem("projectVideoBase64", base64String);
                    };
                    reader.readAsDataURL(file);
                  } else {
                    setFormData((prev: any) => ({
                      ...prev,
                      index_video: "",
                    }));
                    localStorage.removeItem("projectVideoBase64");
                  }
                }}
                storageKey="projectVideoBase64"
                defaultVideo={formData.index_video}
              />
            </div>
          </div>

          <div className="w-full flex justify-end">
            <ToggleSwitch
              label="وضعیت انتشار"
              checked={formData.is_active ?? false}
              onChange={() => {
                setFormData((prev) => ({
                  ...prev,
                  is_active: !prev.is_active,
                }));
              }}
            />
          </div>
          <div className="w-full">
            <PdfUpload onFilesSelect={handlePdfFilesSelect} />
          </div>
          <TextArea
            className="mt-3"
            name="description"
            value={formData.description}
            placeholder="توضیحات "
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                description: e.target.value,
              }));
            }}
            rows={4}
          />

          <TextEditor
            value={formData.content}
            onChange={(content) => {
              setFormData((prev) => ({
                ...prev,
                content: content,
              }));
            }}
          />

          <div className="flex justify-end">
            <Button
              disabled={loading ? true : false}
              type="submit"
              title={loading ? "...انتظار" : "ثبت"}
              variant="success"
              bgColor="#9EE1D1"
              borderColor="#00B087"
              color="#00B087"
            />
          </div>
        </form>
      </Container>
    </div>
  );
}
